import { BallPositionByDot } from '../initials';
import { AnimationChain } from './AnimationChain';
import { StartSimpleFallAnimation } from './StartSimpleFallAnimation';

const getCompiledPath = (path) => {
    const arr = path?.split('');

    const newArr = [];
    for (let i = 0; i < arr.length;) {
        if (arr[i + 1] === '*') {
            newArr.push(`${arr[i]}*`);
            i += 2;
        } else {
            newArr.push(arr[i]);
            i++;
        }
    }

    return newArr;
}

const LeftMoveAnimations = [BallPositionByDot.BOTTOM_LEFT, BallPositionByDot.TOP_LEFT, BallPositionByDot.CENTER];
const RightMoveAnimations = [BallPositionByDot.BOTTOM_RIGHT, BallPositionByDot.TOP_RIGHT, BallPositionByDot.CENTER];

export class AnimationChainFactory {
    constructor({
        availableAnimations,
        disabledChains,
        oneLevelAnimations,
        disabledDirection,
        finalAnimations,
    }) {
        this.availableAnimations = availableAnimations;
        this.disabledChains = disabledChains;
        this.oneLevelAnimations = oneLevelAnimations;
        this.disabledDirection = disabledDirection;
        this.finalAnimations = finalAnimations;
    }

    getFilteredAnimationList({
        animationList,
        prevAnimation,
        nextPartOfPath
    }) {
        const prevAnimationSettings = prevAnimation?.constructor.getAnimationSettings();

        if (!prevAnimationSettings) {
            return animationList;
        }

        const filteredAnimations = animationList.filter(animation => {
            const currentSettings = animation.getAnimationSettings();
            const disabledDirections = this.disabledDirection[prevAnimationSettings.toBallPositionByDot] ?? [];

            if (disabledDirections.includes(currentSettings.toBallPositionByDot)) {
                return false;
            }

            if (currentSettings.fromBallPositionByDot !== prevAnimationSettings.toBallPositionByDot) {
                return false;
            }

            if (!nextPartOfPath) {
                return true;
            }

            const isNextStepMoveLeft = nextPartOfPath.includes("0");

            return isNextStepMoveLeft
                ? LeftMoveAnimations.includes(currentSettings.toBallPositionByDot)
                : RightMoveAnimations.includes(currentSettings.toBallPositionByDot);
        });

        return filteredAnimations;

        // return animationList;
    }

    build({
        path,
        dotsArr,
        delay,
    }) {
        if (!path?.length) {
            return new AnimationChain([]);
        }
        const availableForMultipleLevelAnimations = this.availableAnimations.filter(x => x.getAnimationSettings().avaiableForMultipleLevels);
        const compiledPath = getCompiledPath(path);

        const startAnimations = [
            new StartSimpleFallAnimation({
                toRowIndex: 0,
                toDotIndex: 1,
                dots: dotsArr,
            }),
            new StartSimpleFallAnimation({
                toRowIndex: 1,
                toDotIndex: compiledPath[0] === '0' ? 1 : 2,
                dots: dotsArr,
            }),
        ];

        const startAnimationIndex = Math.floor(Math.random() * startAnimations.length);
        const startAnimation = startAnimations[startAnimationIndex];

        const actualCompiledPath = [...compiledPath];
        actualCompiledPath.splice(0, startAnimation.toRowIndex);

        const result = actualCompiledPath.reduce((agg, partOfPath, i) => {
            if (agg.level === dotsArr.length) {
                return { ...agg };
            }

            const nextLevel = actualCompiledPath[i + 1]?.length === 2 ? agg.level : agg.level + 1;

            if (partOfPath.length === 2) {
                const newDotIndex = agg.prevDotIndex + (partOfPath[0] === '0' ? -1 : 1);

                const filteredAnimations = this.getFilteredAnimationList({
                    animationList: this.oneLevelAnimations,
                    prevAnimation: agg.animations[agg.animations.length - 1],
                    nextPartOfPath: actualCompiledPath[i + 1],
                });

                const randomAnimationIndex = Math.floor(Math.random() * filteredAnimations.length);
                const Animation = filteredAnimations[randomAnimationIndex];

                const animation = new Animation({
                    fromRowIndex: agg.level,
                    fromDotIndex: agg.prevDotIndex,
                    toRowIndex: agg.level,
                    toDotIndex: newDotIndex,
                    dots: dotsArr,
                });

                return {
                    animations: [...agg.animations, animation],
                    prevDotIndex: newDotIndex,
                    prevAnimationRowIndex: agg.level,
                    prevAnimationDotIndex: newDotIndex,
                    level: nextLevel,
                };
            }

            const newIndex = agg.prevDotIndex + parseInt(partOfPath);
            const shouldAddAimation = actualCompiledPath[i] !== actualCompiledPath[i + 1] || agg.level === startAnimation.toRowIndex + 1 || agg.level === dotsArr.length - 1;

            if (!shouldAddAimation) {
                return {
                    animations: agg.animations,
                    prevDotIndex: newIndex,
                    prevAnimationRowIndex: agg.prevAnimationRowIndex,
                    prevAnimationDotIndex: agg.prevAnimationDotIndex,
                    level: nextLevel,
                };
            }

            const animationArray = agg.level - agg.prevAnimationRowIndex > 1 ? availableForMultipleLevelAnimations : this.availableAnimations;

            // TODO: move to filter method
            const filteredArray = animationArray.filter(item => {
                const prevType = agg.animations[agg.animations.length - 1]?.constructor.getAnimationSettings().type;
                const disabledTypes = prevType && this.disabledChains[prevType];
                return !disabledTypes || !disabledTypes.includes(item.getAnimationSettings().type);
            });

            const filteredAnimations = this.getFilteredAnimationList({
                animationList: filteredArray,
                prevAnimation: agg.animations[agg.animations.length - 1],
                nextPartOfPath: actualCompiledPath[i + 1],
            });

            const randomAnimationIndex = Math.floor(Math.random() * filteredAnimations.length);
            const Animation = filteredAnimations[randomAnimationIndex];
            const animationInstance = new Animation({
                fromRowIndex: agg.prevAnimationRowIndex,
                fromDotIndex: agg.prevAnimationDotIndex,
                toRowIndex: agg.level,
                toDotIndex: newIndex,
                dots: dotsArr,
            });

            return {
                animations: [...agg.animations, animationInstance],
                prevDotIndex: newIndex,
                prevAnimationRowIndex: agg.level,
                prevAnimationDotIndex: newIndex,
                level: nextLevel
            };
        }, {
            animations: [startAnimation],
            prevDotIndex: startAnimation.toDotIndex,
            prevAnimationRowIndex: startAnimation.toRowIndex,
            prevAnimationDotIndex: startAnimation.toDotIndex,
            level: startAnimation.toRowIndex + (actualCompiledPath[0].length === 1 ? 1 : 0)
        });

        const filteredFinalAnimations = this.getFilteredAnimationList({
            animationList: this.finalAnimations,
            prevAnimation: result.animations[result.animations.length - 1],
            nextPartOfPath: null,
        });
        const randomEndAnimationIndex = Math.floor(Math.random() * filteredFinalAnimations.length)
        const FinalAnimation = filteredFinalAnimations[randomEndAnimationIndex];

        return new AnimationChain([
            ...result.animations,
            new FinalAnimation({
                fromRowIndex: result.prevAnimationRowIndex,
                fromDotIndex: result.prevAnimationDotIndex,
                isLeftMove: compiledPath[compiledPath.length - 1] === '0',
                dots: dotsArr,
            }),
        ], {
            delay,
        });
    }
}